import type { Study } from '@pocketprep/types'
import { Parse, runCloudFunction } from '@/store/parseUtils'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { fetchLoadable, resetLoadable } from '@/store/utils'
import { userModule } from '@/store/user/module'
import { progressModule } from '@/store/progress/module'
import { analyticsModule } from '@/store/analytics/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'

const deleteUserExamMetadata = async (userExamMetadata: Study.Class.UserExamMetadataJSON) => {
    const uem = await new Parse.Query<Study.Class.UserExamMetadata>('UserExamMetadata').get(userExamMetadata.objectId)
    await uem.destroy()
    await fetchUserExamMetadata(true)
}

const resetExamProgress = async (examMetadata: Study.Class.ExamMetadataJSON | ClientExam) => {
    const currentUser = userModule.state.user
    if (!currentUser) {
        throw new Error('No User found')
    }
    const uem = userExamMetadataModule.getters.getCurrentUserExamMetadata()
    if (!uem) {
        throw new Error('No UserExamMetadata found.')
    }

    // Clear out readiness report eligibility flag for user's exam
    type UserId = string
    type ExamMetadataId = string
    const readinessReportEligibility: Record<UserId, ExamMetadataId[] | undefined> = JSON.parse(
        localStorage.getItem('readinessReportEligibility') || '{}'
    )
    const userEligibleExamIds = readinessReportEligibility[currentUser.objectId] || []
    const indexToRemove = userEligibleExamIds.indexOf(examMetadata.objectId)
    if (indexToRemove !== -1) {
        userEligibleExamIds.splice(indexToRemove, 1)
        readinessReportEligibility[currentUser.objectId] = userEligibleExamIds
        localStorage.setItem('readinessReportEligibility', JSON.stringify(readinessReportEligibility))
    }

    await runCloudFunction<Study.Cloud.resetQuizProgress>('resetQuizProgress', {
        examGuid: examMetadata.examGuid,
        examMetadataId: examMetadata.objectId,
    })
    resetLoadable(userModule.state.userData)
    resetLoadable(userExamMetadataModule.state.userExamMetadata)
    await userModule.actions.fetchUserData()

    // if this is an exam you aren't actively studying, reset and refetch all progress
    if (uem.examGuid !== examMetadata.examGuid) {
        resetLoadable(progressModule.state.progressByExamGuid)
        await progressModule.actions.fetchProgress()
    }

    analyticsModule.actions.updateIntercom()
}

export type TUpsertUserExamMetadata =
    (Partial<Study.Class.UserExamMetadataPayload> & { objectId: string })
    | (Omit<Study.Class.UserExamMetadataPayload, 'user'> & { user?: Study.Class.User | Parse.Pointer })
    | { objectId: string; scheduledExamDate?: Date | null }
const upsertUserExamMetadata = async (
    params: TUpsertUserExamMetadata,
    options?: { forceInsert?: boolean }
) => {
    // Need to ensure Parse.User.current() is up to date
    await userModule.actions.refreshParseUser()
    const currentUser = Parse.User.current()

    if (!currentUser) {
        throw new Error('No user found.')
    }

    // if no objectId is passed, check that user does not already have a UEM record for that examGuid
    let uem: Study.Class.UserExamMetadataJSON | undefined
    if (!options?.forceInsert && !params.objectId && 'examGuid' in params && params.examGuid) {
        uem = userExamMetadataModule.getters.getUserExamMetadataForExamGuid(params.examGuid)
        params.objectId = uem?.objectId
    }

    const updateUserExamMetadata = new Parse.Object<TUpsertUserExamMetadata>('UserExamMetadata', {
            ...params,
            user: currentUser as Study.Class.User,
        }),
        userACL = new Parse.ACL(currentUser)

    updateUserExamMetadata.setACL(userACL)

    await updateUserExamMetadata.save()

    await fetchUserExamMetadata(true)

    analyticsModule.actions.updateIntercom()
}

const setScheduledExamDate = async (params: {
    scheduledExamDate: Date | null
    isScheduledExamDateApprox?: boolean
    examNativeName: string
    uemCreatedAt: string
    uemObjectId: string
    bundle: Study.Class.BundleJSON | ClientBundle
    hasSubscription: boolean
}) => {
    return upsertUserExamMetadata({
        objectId: params.uemObjectId,
        scheduledExamDate: params.scheduledExamDate,
        isScheduledExamDateApprox: params.isScheduledExamDateApprox ?? false,
    })
}

const fetchUserExamMetadata = async (forceFetch?: boolean) => {
    const currentUser = userModule.state.user

    if (currentUser) {
        await fetchLoadable(userExamMetadataModule.state.userExamMetadata, async () => {
            return runCloudFunction<Study.Cloud.fetchUserExamMetadata>('fetchUserExamMetadata')
        }, forceFetch)
    }
}

const toggleQuestionFlag = async (serial?: string) => {
    if (!serial) {
        throw new Error('Unable to flag question without a serial')
    }

    // Prevent additional calls if already processing previous toggleQuestionFlag call
    // OR if called too soon between last call -> 750 milliseconds between calls
    const throttleDelay = 750
    const now = Date.now()
    if (
        userExamMetadataModule.state.isTogglingFlag
        || (now - userExamMetadataModule.state.lastTimeTogglingFlag < throttleDelay)
    ) {
        return
    }

    userExamMetadataModule.state.lastTimeTogglingFlag = now
    userExamMetadataModule.state.isTogglingFlag = true

    const uem = userExamMetadataModule.getters.getCurrentUserExamMetadata()
    if (!uem) {
        userExamMetadataModule.state.isTogglingFlag = false
        throw new Error('No UserExamMetadata found.')
    }

    const parseUEM = new Parse.Object('UserExamMetadata')
    parseUEM.set('objectId', uem.objectId)

    const currentFlaggedQs = uem.flaggedQuestions || []
    if (currentFlaggedQs.includes(serial)) {
        parseUEM.remove('flaggedQuestions', serial)
    } else {
        parseUEM.addUnique('flaggedQuestions', serial)
    }

    await parseUEM.save()
    await fetchUserExamMetadata(true)

    userExamMetadataModule.state.isTogglingFlag = false
}

const startNewLevels = async ({ subjectIds }: {
    subjectIds: string[]
}) => {
    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()

    if (currentExam) {
        const levelUpProgress = await runCloudFunction<Study.Cloud.startNewLevels>('startNewLevels', {
            examGuid: currentExam.examGuid,
            majorVersion: currentExam.version.split('.')[0],
            subjectIds,
        })

        await fetchUserExamMetadata(true)

        return levelUpProgress
    }
}

const resetLevelUpProgress = async () => {
    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
    const examGuid = currentExam?.examGuid
    const majorVersion = currentExam?.version.split('.')[0]
    const examMetadataId = currentExam?.objectId
    if (examGuid && majorVersion) {
        await runCloudFunction<Study.Cloud.resetLevelUpProgress>('resetLevelUpProgress', {
            examGuid,
            examMetadataId,
            majorVersion,
        })

        await fetchUserExamMetadata(true)
    }
}

const submitExamResult = async (params: {
    examMetadataId: string
    passed?: boolean
}) => {
    const { examMetadataId, passed } = params
    await runCloudFunction<Study.Cloud.submitExamResult>('submitExamResult', {
        examMetadataId,
        passed,
    })

    // Server will clear out scheduled exam date if false or undefined is passed as the passed param
    // Refetch UserExamMetadata so we do not display that the user still has a set exam date
    // If they failed their exam or did not take it (undefined)
    if (!passed) {
        await fetchUserExamMetadata(true)
    }
}

const upsertUserExamBackground = async (params: {
    examMetadataId: string
    hasTakenExam: boolean
}) => {
    const { examMetadataId, hasTakenExam } = params
    await runCloudFunction<Study.Cloud.upsertUserExamBackground>('upsertUserExamBackground', {
        examMetadataId,
        hasTakenExam,
    })
}

export default {
    upsertUserExamMetadata,
    setScheduledExamDate,
    fetchUserExamMetadata,
    resetExamProgress,
    deleteUserExamMetadata,
    toggleQuestionFlag,
    startNewLevels,
    resetLevelUpProgress,
    submitExamResult,
    upsertUserExamBackground,
}
